import { useState, useEffect, useMemo } from 'react';
import { Pencil, Copy, XCircle } from 'lucide-react';
import { getClasses, createClass, deleteClass, updateClass, removeStudentFromClass, updatePass, updateBookingOnline, updateBookingAttended, getUserDetails, removeFromWaitlist } from '../../services/adminService';
import { useAppContext } from '../../context/AppContext';
import CreateClassModal from './classes/CreateClassModal';
import AddStudentModal from './classes/AddStudentModal';
import ClassSignupsModal from './classes/ClassSignupsModal';
import UserDetailsModal from './users/UserDetailsModal';

export default function AdminClasses() {
  const { setToaster, token } = useAppContext();
  const [classes, setClasses] = useState([]);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState(null);
  const [selectedClass, setSelectedClass] = useState(null);
  const [classBookings, setClassBookings] = useState([]);
  const [bookingsLoading, setBookingsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('onsite');
  const [showAddStudent, setShowAddStudent] = useState(false);
  const [showClassModal, setShowClassModal] = useState(false);
  const [editingClass, setEditingClass] = useState(null);
  const [isDuplicateMode, setIsDuplicateMode] = useState(false);
  const [userModalUserId, setUserModalUserId] = useState(null);
  const [userDetails, setUserDetails] = useState(null);
  const [userDetailsLoading, setUserDetailsLoading] = useState(false);

  useEffect(() => {
    fetchClasses();
  }, [page]);

  const fetchClasses = async () => {
    setLoading(true);
    try {
      const data = await getClasses(token, page);
      setClasses(data.classes);
      setPagination(data.pagination);
    } catch (error) {
      console.error('Error fetching classes:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleViewSignups = async (classObj) => {
    setBookingsLoading(true);
    try {
      const data = await getClasses(token, 1);
      const fullClassDetails = data.classes.find(c => c._id === classObj._id) || classObj;

      setSelectedClass(fullClassDetails);
      setActiveTab(fullClassDetails.type === 'online' ? 'online' : 'onsite');

      if (fullClassDetails.bookings) {
        const formattedBookings = fullClassDetails.bookings.map(booking => ({
          _id: booking._id,
          userId: booking.userId,
          status: booking.status,
          bookedAt: booking.bookedAt,
          canceledAt: booking.canceledAt || null,
          autoBooked: booking.autoBooked,
          online: booking.online === true,
          attended: booking.attended,
          unpaidPass: booking.unpaidPass === true,
          passId: booking.passId || null
        }));
        setClassBookings(formattedBookings);
      } else {
        setClassBookings([]);
      }
    } catch (error) {
      console.error('Error fetching class details:', error);
    } finally {
      setBookingsLoading(false);
    }
  };

  const handleCancelBooking = async (classId, userId) => {
    if (!window.confirm('Are you sure you want to cancel this booking?')) return;

    try {
      await removeStudentFromClass(token, classId, userId);
      fetchClasses();
      if (selectedClass && selectedClass._id === classId) {
        handleViewSignups(selectedClass);
      }
      setToaster({
        message: "Booking cancelled successfully!",
        type: "success",
        show: true,
      });
    } catch (error) {
      console.error('Error cancelling booking:', error);
      setToaster({
        message: "Failed to cancel booking",
        type: "error",
        show: true,
      });
    }
  };

  const handleToggleOnline = async (classId, bookingId, online) => {
    try {
      await updateBookingOnline(token, classId, bookingId, { online });
      fetchClasses();
      if (selectedClass && selectedClass._id === classId) {
        handleViewSignups(selectedClass);
      }
      setToaster({
        message: `Booking set to ${online ? 'online' : 'in-studio'}`,
        type: "success",
        show: true,
      });
    } catch (err) {
      console.error('Error updating booking online:', err);
      setToaster({
        message: err.message || "Failed to update booking",
        type: "error",
        show: true,
      });
    }
  };

  const handleToggleAttended = async (classId, bookingId, attended) => {
    try {
      await updateBookingAttended(token, classId, bookingId, { attended });
      fetchClasses();
      if (selectedClass && selectedClass._id === classId) {
        handleViewSignups(selectedClass);
      }
      setToaster({
        message: attended === false ? "Marked as absent — spot freed" : "Marked as attended",
        type: "success",
        show: true,
      });
    } catch (err) {
      console.error('Error updating booking attended:', err);
      setToaster({
        message: err.message || "Failed to update booking",
        type: "error",
        show: true,
      });
    }
  };

  const handleAddStudentSuccess = () => {
    handleViewSignups(selectedClass);
    fetchClasses();
  };

  const handleCreateClass = async (classData) => {
    try {
      await createClass(token, classData);
      setShowClassModal(false);
      setEditingClass(null);
      setIsDuplicateMode(false);
      fetchClasses();
      setToaster({
        message: "Class created successfully!",
        type: "success",
        show: true,
      });
    } catch (error) {
      setToaster({
        message: "Failed to create class",
        type: "error",
        show: true,
      });
    }
  };

  const handleEditClass = (classObj) => {
    setEditingClass(classObj);
    setIsDuplicateMode(false);
    setShowClassModal(true);
  };

  const handleDuplicateClass = (classObj) => {
    setEditingClass(classObj);
    setIsDuplicateMode(true);
    setShowClassModal(true);
  };

  const handleUpdateClass = async (classData) => {
    try {
      await updateClass(token, classData._id, classData);
      setShowClassModal(false);
      setEditingClass(null);
      setIsDuplicateMode(false);
      fetchClasses();
      setToaster({
        message: "Class updated successfully!",
        type: "success",
        show: true,
      });
    } catch (error) {
      setToaster({
        message: "Failed to update class",
        type: "error",
        show: true,
      });
    }
  };

  const handleClassModalSubmit = (classData) => {
    if (isDuplicateMode) {
      handleCreateClass(classData);
    } else if (editingClass) {
      handleUpdateClass(classData);
    } else {
      handleCreateClass(classData);
    }
  };

  const closeClassModal = () => {
    setShowClassModal(false);
    setEditingClass(null);
    setIsDuplicateMode(false);
  };

  const handleDeleteClass = async (classId) => {
    if (!window.confirm('Are you sure you want to cancel this class? All bookings will be refunded.')) return;
    
    try {
      await deleteClass(token, classId);
      fetchClasses();
      setToaster({
        message: "Class cancelled successfully!",
        type: "success",
        show: true,
      });
    } catch (error) {
      setToaster({
        message: "Failed to cancel class",
        type: "error",
        show: true,
      });
    }
  };

  const closeModal = () => {
    setSelectedClass(null);
    setClassBookings([]);
    setActiveTab('onsite');
  };

  const handleViewUserDetails = async (userId) => {
    if (!userId) return;
    setUserModalUserId(userId);
    setUserDetailsLoading(true);
    try {
      const data = await getUserDetails(token, userId);
      setUserDetails(data);
    } catch (error) {
      console.error('Error fetching user details:', error);
      setToaster({
        message: 'Failed to load user',
        type: 'error',
        show: true,
      });
      setUserModalUserId(null);
      setUserDetails(null);
    } finally {
      setUserDetailsLoading(false);
    }
  };

  const closeUserDetailsModal = () => {
    setUserModalUserId(null);
    setUserDetails(null);
  };

  const handleRemoveFromWaitlist = async (classId, userId) => {
    try {
      await removeFromWaitlist(token, classId, userId);
      fetchClasses();
      if (selectedClass && selectedClass._id === classId) {
        handleViewSignups(selectedClass);
      }
      setToaster({
        message: "Removed from waiting list",
        type: "success",
        show: true,
      });
    } catch (error) {
      console.error('Error removing from waitlist:', error);
      setToaster({
        message: error.message || "Failed to remove from waiting list",
        type: "error",
        show: true,
      });
    }
  };

  const getBookingCounts = () => {
    const counts = {};
    classBookings.forEach(b => {
      const id = b.userId?._id || b.userId;
      if (!id) return;
      counts[id] = (counts[id] || 0) + 1;
    });
    return counts;
  };

  // Same emoji + number breakdown as admin desktop calendar cell (online vs in-studio)
  const getBookingCountsForClass = (classObj) => {
    const bookings = classObj?.bookings || [];
    const confirmed = bookings.filter((b) => b.status === "confirmed");
    const online = confirmed.filter((b) => b.online === true && b.attended !== false).length;
    const offline = confirmed.filter((b) => !b.online && b.attended !== false).length;
    return { online, offline };
  };

  const renderBookingsCell = (classObj) => {
    const { online, offline } = getBookingCountsForClass(classObj);
    const total = classObj.bookingCount ?? classObj.bookedCount ?? (online + offline);
    const waitingCount = (classObj.waitingList || []).length;
    if (online === 0 && offline === 0 && total === 0 && waitingCount === 0) {
      return null;
    }
    return (
      <div className="flex items-center gap-1.5 whitespace-nowrap">
        {online > 0 && <span className="text-blue-600 text-xs" title="Online">🖥️ {online}</span>}
        {offline > 0 && <span className="text-green-700 text-xs" title="In studio">🏠 {offline}</span>}
        {waitingCount > 0 && <span className="text-amber-600 text-xs" title="Waiting list">⏳ {waitingCount}</span>}
      </div>
    );
  };

  // Soonest first, then future classes, then past (most recent past first)
  const sortedClasses = useMemo(() => {
    const now = new Date();

    // We display class date/time in Asia/Jerusalem, so keep "today" pinned based on that same timezone.
    const TZ = 'Asia/Jerusalem';
    const dayKey = (d) => d.toLocaleDateString('en-CA', { timeZone: TZ }); // YYYY-MM-DD
    const todayKey = dayKey(now);

    const bucketRank = (k) => (k === todayKey ? 0 : k > todayKey ? 1 : 2);

    return [...classes].sort((a, b) => {
      const aStart = new Date(a.start);
      const bStart = new Date(b.start);
      const aKey = dayKey(aStart);
      const bKey = dayKey(bStart);

      const ar = bucketRank(aKey);
      const br = bucketRank(bKey);
      if (ar !== br) return ar - br;

      // Today + future days: soonest first. Past days (before today): most recent first.
      if (ar === 2) return bStart - aStart;
      return aStart - bStart;
    });
  }, [classes]);

  const renderClassActions = (classObj, stopPropagation = false) => (
    <div
      className="flex gap-2 items-center flex-shrink-0"
      onClick={stopPropagation ? (e) => e.stopPropagation() : undefined}
      role={stopPropagation ? 'presentation' : undefined}
    >
      <button
        onClick={() => handleEditClass(classObj)}
        title="Edit"
        className="p-2 rounded text-blue-600 hover:bg-blue-600/10 transition-colors touch-manipulation"
      >
        <Pencil size={18} />
      </button>
      <button
        onClick={() => handleDuplicateClass(classObj)}
        title="Duplicate"
        className="p-2 rounded text-green-600 hover:bg-green-600/10 transition-colors touch-manipulation"
      >
        <Copy size={18} />
      </button>
      <button
        onClick={() => handleDeleteClass(classObj._id)}
        title="Cancel class"
        className="p-2 rounded text-red-600 hover:bg-red-600/10 transition-colors touch-manipulation"
      >
        <XCircle size={18} />
      </button>
    </div>
  );

  const dateTimeStr = (classObj) =>
    new Date(classObj.start).toLocaleString('en-GB', {
      timeZone: 'Asia/Jerusalem',
      weekday: 'short',
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });

  const handleRefreshWithAutoBook = async () => {
    setLoading(true);
    try {
      const data = await getClasses(token, page, true);
      setClasses(data.classes);
      setPagination(data.pagination);
      setToaster({
        message: "Classes refreshed; regular-day auto-book run for future classes.",
        type: "success",
        show: true,
      });
    } catch (error) {
      console.error("Error refreshing classes:", error);
      setToaster({ message: "Failed to refresh", type: "error", show: true });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow p-4 sm:p-6">
      <div className="mb-4 flex justify-end gap-2 flex-wrap">
        <button
          type="button"
          onClick={handleRefreshWithAutoBook}
          className="w-full sm:w-auto border border-brown text-brown px-4 py-2.5 rounded hover:bg-brown/10 text-sm sm:text-base touch-manipulation"
        >
          Refresh (auto-book regular days)
        </button>
        <button
          onClick={() => {
            setEditingClass(null);
            setShowClassModal(true);
          }}
          className="w-full sm:w-auto bg-brown text-white px-4 py-2.5 rounded hover:bg-pink text-sm sm:text-base touch-manipulation"
        >
          + Create Class
        </button>
      </div>

      {/* Desktop: Table */}
      <div className="hidden md:block overflow-x-auto">
        <table className="w-full text-left text-sm min-w-[640px]">
          <thead className="border-b border-gray-200 bg-gray-50">
            <tr>
              <th className="py-3 px-4 font-semibold">Title</th>
              <th className="py-3 px-4 font-semibold">Location</th>
              <th className="py-3 px-4 font-semibold">Date & Time</th>
              <th className="py-3 px-4 font-semibold">Capacity</th>
              <th className="py-3 px-4 font-semibold">Bookings</th>
              <th className="py-3 px-4 font-semibold">Actions</th>
            </tr>
          </thead>
          <tbody>
            {sortedClasses.map((classObj) => {
              const isPast = new Date(classObj.start) < new Date();
              return (
                <tr
                  key={classObj._id}
                  role="button"
                  tabIndex={0}
                  onClick={() => handleViewSignups(classObj)}
                  onKeyDown={(e) => e.key === 'Enter' && handleViewSignups(classObj)}
                  className={`
                    border-b border-gray-200 hover:bg-gray-50 cursor-pointer
                    ${isPast ? "bg-gray-100 text-gray-400 opacity-70" : ""}
                  `}
                >
                  <td className="py-3 px-4 font-semibold">
                    {classObj.title || classObj.description}
                    <span className="ml-2 inline-block rounded bg-gray-100 px-1.5 py-0.5 text-xs font-normal text-gray-600 capitalize">
                      {classObj.classType?.displayName || 'Studio'}
                    </span>
                  </td>
                  <td className="py-3 px-4">{classObj.locationName}</td>
                  <td className="py-3 px-4">{dateTimeStr(classObj)}</td>
                  <td className="py-3 px-4">{classObj.capacity}</td>
                  <td className="py-3 px-4">
                    {renderBookingsCell(classObj)}
                  </td>
                  <td className="py-3 px-4">{renderClassActions(classObj, true)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Mobile: Cards */}
      <div className="md:hidden space-y-3">
        {sortedClasses.map((classObj) => {
          const isPast = new Date(classObj.start) < new Date();
          return (
            <div
              key={classObj._id}
              role="button"
              tabIndex={0}
              onClick={() => handleViewSignups(classObj)}
              onKeyDown={(e) => e.key === 'Enter' && handleViewSignups(classObj)}
              className={`
                border border-gray-200 rounded-lg p-4 cursor-pointer hover:bg-gray-50
                ${isPast ? "bg-gray-50 text-gray-500 opacity-30" : "bg-white"}
              `}
            >
              <div className="flex justify-between items-start gap-2 mb-2">
                <h3 className="font-semibold text-gray-900 text-sm leading-tight flex-1 min-w-0">
                  {classObj.title || classObj.description}
                </h3>
                {renderClassActions(classObj, true)}
              </div>
              <div className="space-y-1 text-xs text-gray-600">
                <p>
                  {classObj.locationName}
                  <span className="ml-2 inline-block rounded bg-gray-100 px-1.5 py-0.5 text-xs capitalize">
                    {classObj.classType?.displayName || 'Studio'}
                  </span>
                </p>
                <p>{dateTimeStr(classObj)}</p>
                <div className="flex items-center justify-between pt-1">
                  <span>Cap: {classObj.capacity}</span>
                  {renderBookingsCell(classObj)}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {loading && <p className="text-center py-4 text-sm">Loading...</p>}
      {classes.length === 0 && !loading && <p className="text-center py-4 text-gray-500 text-sm">No classes found</p>}

      {pagination && (
        <div className="flex flex-wrap gap-2 mt-4 justify-center items-center">
          <button
            onClick={() => page > 1 && setPage(page - 1)}
            disabled={page === 1}
            className="px-3 py-2 sm:px-4 text-sm border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed touch-manipulation"
          >
            Previous
          </button>
          <span className="px-2 sm:px-4 py-2 text-sm">
            Page {pagination.page} of {pagination.pages}
          </span>
          <button
            onClick={() => page < pagination.pages && setPage(page + 1)}
            disabled={page >= pagination.pages}
            className="px-3 py-2 sm:px-4 text-sm border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed touch-manipulation"
          >
            Next
          </button>
        </div>
      )}

      {/* Class Sign-ups Modal */}
      <ClassSignupsModal
        selectedClass={selectedClass}
        classBookings={classBookings}
        bookingsLoading={bookingsLoading}
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        closeModal={closeModal}
        handleCancelBooking={handleCancelBooking}
        getBookingCounts={getBookingCounts}
        setShowAddStudent={setShowAddStudent}
        handleAddStudentSuccess={handleAddStudentSuccess}
        token={token}
        updatePass={updatePass}
        setToaster={setToaster}
        onRefetchSignups={() => selectedClass && handleViewSignups(selectedClass)}
        onToggleOnline={handleToggleOnline}
        onToggleAttended={handleToggleAttended}
        onOpenUserDetails={handleViewUserDetails}
        onRemoveFromWaitlist={handleRemoveFromWaitlist}
      />

      {userModalUserId && (
        <UserDetailsModal
          userDetails={userDetails}
          detailsLoading={userDetailsLoading}
          onClose={closeUserDetailsModal}
          onRefresh={() => userModalUserId && handleViewUserDetails(userModalUserId)}
          onUserUpdated={() => {
            fetchClasses();
            if (selectedClass) handleViewSignups(selectedClass);
          }}
          onMergeComplete={async (keptUserId) => {
            await fetchClasses();
            if (selectedClass) handleViewSignups(selectedClass);
            if (keptUserId) await handleViewUserDetails(keptUserId);
            else closeUserDetailsModal();
          }}
        />
      )}

      {/* Add Student Modal */}
      {showAddStudent && selectedClass && (
        <AddStudentModal
          classId={selectedClass._id}
          classDetails={selectedClass}
          initialTab={activeTab}
          onClose={() => setShowAddStudent(false)}
          onSuccess={handleAddStudentSuccess}
        />
      )}

      {/* Create/Edit Class Modal */}
      <CreateClassModal
        show={showClassModal}
        onClose={closeClassModal}
        onSubmit={handleClassModalSubmit}
        editClass={editingClass}
        isDuplicate={isDuplicateMode}
        token={token}
      />
    </div>
  );
}